/**
 * PaymentSuccessPage - v1.0 NOWPayments Success Handler
 * Shown after user returns from NOWPayments checkout
 *
 * Route: /q-ai/payments/success
 *
 * Features:
 * - Polls payment status every 3 seconds
 * - Shows real-time status updates
 * - Displays tokens credited on completion
 * - Redirects to Q AI Coach after success
 *
 * Status Flow:
 * pending → processing → accepted (success)
 *                      → rejected/expired (failure)
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import QAIHeader from '../../Components/QAI/QAIHeader';
import PaymentStatusBadge from '../../Components/QAI/PaymentStatusBadge';
import { useTokenBalance } from '../../context/TokenBalanceContext';
import {
    getPaymentStatus,
    getPendingPayment,
    clearPendingPayment,
    isFinalStatus,
    isSuccessStatus
} from '../../api/paymentApi';
import { notifyPaymentComplete, requestBalanceRefresh } from '../../services/qbridge.service';
import { SUPPORT_EMAIL } from '../../config';

// ═══════════════════════════════════════════════════════════════════════════════
// BRAND CONSTANTS (Matching QAILandingPage)
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND = {
    primary: '#27B56D',
    primaryLight: '#22C55E',
    background: '#0A0A0F',
    surface: '#12121A',
    text: '#FFFFFF',
    textSecondary: '#E2E8F0',
    textMuted: '#94A3B8',
    border: 'rgba(255, 255, 255, 0.08)',
    error: '#EF4444',
    warning: '#FBBF24'
};

// Polling configuration
const POLL_INTERVAL = 3000; // 3 seconds
const MAX_POLL_TIME = 15 * 60 * 1000; // 15 minutes max polling

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const PaymentSuccessPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get token balance context to refresh after successful payment
    const { refreshBalance } = useTokenBalance();

    // State
    const [paymentId, setPaymentId] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tokensExpected, setTokensExpected] = useState(null);

    // Refs for polling
    const pollIntervalRef = useRef(null);
    const pollStartTimeRef = useRef(null);

    // ═══════════════════════════════════════════════════════════════════════════
    // STOP POLLING
    // ═══════════════════════════════════════════════════════════════════════════

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }, []);

    // ═══════════════════════════════════════════════════════════════════════════
    // FETCH PAYMENT STATUS
    // ═══════════════════════════════════════════════════════════════════════════

    const fetchPaymentStatus = useCallback(async (id) => {
        try {
            const result = await getPaymentStatus(id);

            if (!result.success) {
                setError(result.error);
                setLoading(false);
                return;
            }

            const status = result.data;
            setPaymentStatus(status);
            setLoading(false);

            console.log('[PaymentSuccessPage] Status update:', {
                status: status.status,
                tokens_credited: status.tokens_credited
            });

            // Check if we should stop polling
            if (isFinalStatus(status.status)) {
                stopPolling();

                // Clear pending payment from localStorage and refresh balance
                if (isSuccessStatus(status.status)) {
                    clearPendingPayment();

                    // Refresh token balance in context after successful payment
                    const balanceResult = await refreshBalance();
                    console.log('[PaymentSuccessPage] Payment successful, refreshing balance');

                    // Notify QBridge so MT5 Panel can update token display
                    // Uses centralized qbridge.service for consistency with QAITokensPage
                    const tokensAdded = status.tokens_credited || status.tokens || 0;
                    const newBalance = balanceResult?.balance || null;
                    await notifyPaymentComplete({
                        tokensAdded,
                        newBalance,
                        transactionId: String(id)
                    });
                    await requestBalanceRefresh();
                }
                return;
            }

            // Check if we've exceeded max poll time
            if (Date.now() - pollStartTimeRef.current > MAX_POLL_TIME) {
                stopPolling();
                setError('Payment verification is taking longer than expected. Please check back later.');
                return;
            }

            // Continue polling
            if (!pollIntervalRef.current) {
                pollIntervalRef.current = setInterval(() => {
                    fetchPaymentStatus(id);
                }, POLL_INTERVAL);
            }
        } catch (err) {
            console.error('[PaymentSuccessPage] Error fetching status:', err);
            setError('Failed to check payment status. Please refresh the page.');
            setLoading(false);
        }
    }, [stopPolling]);

    // ═══════════════════════════════════════════════════════════════════════════
    // INITIALIZE - Get payment ID from URL or localStorage
    // ═══════════════════════════════════════════════════════════════════════════

    useEffect(() => {
        // Check URL params first (NOWPayments may pass payment_id)
        const urlPaymentId = searchParams.get('payment_id') || searchParams.get('orderId');

        // Check localStorage for pending payment
        const pendingPayment = getPendingPayment();

        const id = urlPaymentId || pendingPayment?.payment_id;
        const tokens = pendingPayment?.tokens;

        console.log('[PaymentSuccessPage] Initializing:', {
            urlPaymentId,
            pendingPayment,
            resolvedId: id
        });

        if (!id) {
            setError('No payment found. Please try purchasing tokens again.');
            setLoading(false);
            return;
        }

        setPaymentId(id);
        setTokensExpected(tokens);
        pollStartTimeRef.current = Date.now();

        // Start polling
        fetchPaymentStatus(id);
    }, [searchParams, fetchPaymentStatus]);

    // Cleanup on unmount
    useEffect(() => {
        return () => stopPolling();
    }, [stopPolling]);

    // ═══════════════════════════════════════════════════════════════════════════
    // HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════

    const handleGoToCoach = () => {
        navigate('/q-ai/coach');
    };

    const handleRetry = () => {
        navigate('/q-ai/coach');
    };

    const handleRefresh = () => {
        setLoading(true);
        setError(null);
        pollStartTimeRef.current = Date.now();
        fetchPaymentStatus(paymentId);
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER HELPERS
    // ═══════════════════════════════════════════════════════════════════════════

    const renderIcon = () => {
        if (loading) {
            return <CircularProgress size={64} sx={{ color: BRAND.primary }} />;
        }

        if (error) {
            return <ErrorIcon sx={{ fontSize: 64, color: BRAND.error }} />;
        }

        if (paymentStatus) {
            const status = paymentStatus.status?.toLowerCase();

            if (isSuccessStatus(status)) {
                return <CheckCircleIcon sx={{ fontSize: 80, color: BRAND.primary }} />;
            }

            if (status === 'rejected' || status === 'expired') {
                return <ErrorIcon sx={{ fontSize: 64, color: BRAND.error }} />;
            }

            // Still processing
            return <AccessTimeIcon sx={{ fontSize: 64, color: BRAND.warning }} />;
        }

        return <AccessTimeIcon sx={{ fontSize: 64, color: BRAND.warning }} />;
    };

    const renderTitle = () => {
        if (loading) {
            return 'Verifying Payment...';
        }

        if (error) {
            return 'Payment Error';
        }

        if (paymentStatus) {
            const status = paymentStatus.status?.toLowerCase();

            if (isSuccessStatus(status)) {
                return 'Payment Successful!';
            }

            if (status === 'rejected') {
                return 'Payment Failed';
            }

            if (status === 'expired') {
                return 'Payment Expired';
            }

            if (status === 'partial') {
                return 'Partial Payment Received';
            }

            // pending or processing
            return 'Processing Payment...';
        }

        return 'Checking Payment Status...';
    };

    const renderMessage = () => {
        if (loading) {
            return 'Please wait while we verify your payment with the blockchain...';
        }

        if (error) {
            return error;
        }

        if (paymentStatus) {
            const status = paymentStatus.status?.toLowerCase();

            if (isSuccessStatus(status)) {
                const tokens = paymentStatus.tokens_credited || tokensExpected;
                return `${tokens} Q.AI tokens have been added to your account!`;
            }

            if (status === 'rejected') {
                return 'Your payment was not successful. Please try again or contact support.';
            }

            if (status === 'expired') {
                return 'Your payment session has expired. Please create a new payment.';
            }

            if (status === 'partial') {
                return 'We received a partial payment. Please contact support for assistance.';
            }

            // pending or processing
            return 'Your payment is being processed. This usually takes 1-5 minutes for blockchain confirmation.';
        }

        return 'Checking your payment status...';
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════

    const isSuccess = paymentStatus && isSuccessStatus(paymentStatus.status);
    const isFailed = paymentStatus && (paymentStatus.status?.toLowerCase() === 'rejected' || paymentStatus.status?.toLowerCase() === 'expired');
    const isProcessing = !loading && !error && !isSuccess && !isFailed;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: BRAND.background,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Unified Q.AI Header */}
            <QAIHeader
                showTokens={true}
                showHelp={false}
                showSettings={false}
                showHomeButton={true}
            />

            {/* Page Content */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3
                }}
            >
            {/* Main Card */}
            <Paper
                elevation={0}
                sx={{
                    background: BRAND.surface,
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 3,
                    p: { xs: 3, md: 5 },
                    maxWidth: 480,
                    width: '100%',
                    textAlign: 'center'
                }}
            >
                {/* Icon */}
                <Box sx={{ mb: 3 }}>
                    {renderIcon()}
                </Box>

                {/* Title */}
                <Typography
                    variant="h4"
                    sx={{
                        color: BRAND.text,
                        fontWeight: 600,
                        mb: 2,
                        fontSize: { xs: '1.5rem', md: '1.75rem' }
                    }}
                >
                    {renderTitle()}
                </Typography>

                {/* Status Badge */}
                {paymentStatus && (
                    <Box sx={{ mb: 2 }}>
                        <PaymentStatusBadge status={paymentStatus.status} />
                    </Box>
                )}

                {/* Message */}
                <Typography
                    sx={{
                        color: BRAND.textMuted,
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        mb: 3
                    }}
                >
                    {renderMessage()}
                </Typography>

                {/* Payment Details */}
                {paymentStatus && (
                    <Box
                        sx={{
                            background: 'rgba(255, 255, 255, 0.02)',
                            borderRadius: 2,
                            p: 2,
                            mb: 3
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ color: BRAND.textMuted, fontSize: '0.85rem' }}>
                                Payment ID
                            </Typography>
                            <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.85rem', fontFamily: 'monospace' }}>
                                #{paymentId}
                            </Typography>
                        </Box>
                        {paymentStatus.price_amount && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ color: BRAND.textMuted, fontSize: '0.85rem' }}>
                                    Amount
                                </Typography>
                                <Typography sx={{ color: BRAND.textSecondary, fontSize: '0.85rem' }}>
                                    ${paymentStatus.price_amount?.toFixed(2)} USD
                                </Typography>
                            </Box>
                        )}
                        {(paymentStatus.tokens_credited || tokensExpected) && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ color: BRAND.textMuted, fontSize: '0.85rem' }}>
                                    Tokens
                                </Typography>
                                <Typography
                                    sx={{
                                        color: isSuccess ? BRAND.primary : BRAND.textSecondary,
                                        fontSize: '0.85rem',
                                        fontWeight: isSuccess ? 600 : 400
                                    }}
                                >
                                    {isSuccess ? '+' : ''}{paymentStatus.tokens_credited || tokensExpected} Q.AI
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Processing indicator */}
                {isProcessing && (
                    <Alert
                        severity="info"
                        icon={<AccessTimeIcon />}
                        sx={{
                            mb: 3,
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            color: '#93C5FD',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                            '& .MuiAlert-icon': { color: '#3B82F6' }
                        }}
                    >
                        Auto-refreshing every 3 seconds...
                    </Alert>
                )}

                {/* Error Alert */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{
                            mb: 3,
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            color: '#FCA5A5',
                            border: '1px solid rgba(239, 68, 68, 0.2)'
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {isSuccess && (
                        <>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<ChatIcon />}
                                onClick={handleGoToCoach}
                                sx={{
                                    backgroundColor: BRAND.primary,
                                    color: '#000',
                                    fontWeight: 600,
                                    py: 1.5,
                                    '&:hover': {
                                        backgroundColor: BRAND.primaryLight
                                    }
                                }}
                            >
                                Start Chatting with Q.AI
                            </Button>
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<ReceiptLongIcon />}
                                onClick={() => navigate('/q-ai/payments/history')}
                                sx={{
                                    color: BRAND.textMuted,
                                    '&:hover': {
                                        color: BRAND.textSecondary
                                    }
                                }}
                            >
                                View Transaction History
                            </Button>
                        </>
                    )}

                    {isFailed && (
                        <>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<RefreshIcon />}
                                onClick={handleRetry}
                                sx={{
                                    backgroundColor: BRAND.primary,
                                    color: '#000',
                                    fontWeight: 600,
                                    py: 1.5,
                                    '&:hover': {
                                        backgroundColor: BRAND.primaryLight
                                    }
                                }}
                            >
                                Try Again
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<HomeIcon />}
                                onClick={() => navigate('/q-ai/coach')}
                                sx={{
                                    borderColor: BRAND.border,
                                    color: BRAND.textSecondary,
                                    py: 1.5,
                                    '&:hover': {
                                        borderColor: BRAND.textMuted,
                                        backgroundColor: 'rgba(255, 255, 255, 0.02)'
                                    }
                                }}
                            >
                                Return to Q.AI
                            </Button>
                        </>
                    )}

                    {isProcessing && (
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            sx={{
                                borderColor: BRAND.border,
                                color: BRAND.textSecondary,
                                py: 1.5,
                                '&:hover': {
                                    borderColor: BRAND.textMuted,
                                    backgroundColor: 'rgba(255, 255, 255, 0.02)'
                                }
                            }}
                        >
                            Refresh Status
                        </Button>
                    )}

                    {(error || isProcessing) && (
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => navigate('/q-ai/coach')}
                            sx={{
                                color: BRAND.textMuted,
                                '&:hover': {
                                    color: BRAND.textSecondary
                                }
                            }}
                        >
                            Return to Q.AI Coach
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Help text */}
            <Typography
                sx={{
                    color: BRAND.textMuted,
                    fontSize: '0.8rem',
                    mt: 3,
                    textAlign: 'center'
                }}
            >
                Having issues? Contact support at {SUPPORT_EMAIL}
            </Typography>
            </Box>
        </Box>
    );
};

export default PaymentSuccessPage;
